import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../components/Auth';
import { LogOut, Mail, Clock, LayoutDashboard, MessageSquare, ChevronDown, ChevronUp, Bot, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_bf547210-49b6-4258-a274-797828f214e8/artifacts/dpk5g0me_logo.png";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedChatId, setExpandedChatId] = useState(null);
  const [chatTranscripts, setChatTranscripts] = useState({});
  const [loadingChats, setLoadingChats] = useState({});

  const fetchContacts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/contacts`, { withCredentials: true });
      setContacts(response.data);
    } catch (error) {
      toast.error("Failed to load contact submissions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const toggleTranscript = async (chatId) => {
    if (expandedChatId === chatId) {
      setExpandedChatId(null);
      return;
    }
    
    setExpandedChatId(chatId);
    
    if (!chatTranscripts[chatId]) {
      setLoadingChats(prev => ({...prev, [chatId]: true}));
      try {
        const response = await axios.get(`${API}/chat/${chatId}`);
        setChatTranscripts(prev => ({...prev, [chatId]: response.data}));
      } catch (error) {
        // Non-critical: transcript failed to load.
      } finally {
        setLoadingChats(prev => ({...prev, [chatId]: false}));
      }
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Sidebar / Header (Simple top nav for dashboard) */}
      <nav className="glass border-b border-white/5 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="305 KAOS PRODUCTIONS" className="h-8 w-auto" />
              <div className="h-4 w-px bg-white/20 hidden sm:block"></div>
              <div className="hidden sm:flex items-center gap-2 text-cyan-400 font-semibold tracking-wider text-sm">
                <LayoutDashboard className="w-4 h-4" />
                ADMIN DASHBOARD
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-full border border-white/10">
                {user?.picture ? (
                  <img src={user.picture} alt="Avatar" className="w-6 h-6 rounded-full" />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center text-xs font-bold">
                    {user?.name?.charAt(0) || 'A'}
                  </div>
                )}
                <span className="text-sm font-medium hidden sm:block">{user?.name}</span>
              </div>
              <button 
                onClick={logout}
                className="text-gray-400 hover:text-red-400 transition-colors p-2 rounded-md hover:bg-red-400/10"
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Lead Inbox</h1>
          <p className="text-gray-400">View and manage contact form submissions and AI chat transcripts.</p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : contacts.length === 0 ? (
          <div className="glass-card border border-white/5 rounded-2xl p-12 text-center">
            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-gray-500" />
            </div>
            <h3 className="text-xl font-bold mb-2">No leads yet</h3>
            <p className="text-gray-400 max-w-sm mx-auto">When someone submits the contact form on your landing page, it will appear here.</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2">
            {contacts.map((contact) => (
              <div key={contact.id} className="glass-card border border-white/10 rounded-2xl overflow-hidden hover:border-cyan-500/30 transition-colors group flex flex-col">
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-white mb-1 truncate" title={contact.name}>{contact.name}</h3>
                      <a href={`mailto:${contact.email}`} className="text-sm text-cyan-400 hover:underline flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {contact.email}
                      </a>
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-1 whitespace-nowrap bg-black/50 px-2 py-1 rounded-md border border-white/5">
                      <Clock className="w-3 h-3" />
                      {formatDate(contact.created_at)}
                    </div>
                  </div>
                  <div className="bg-black/40 border border-white/5 rounded-lg p-4 text-gray-300 text-sm h-32 overflow-y-auto custom-scrollbar">
                    {contact.message}
                  </div>
                </div>
                
                {contact.chat_id && (
                  <div className="border-t border-white/5 bg-black/30">
                    <button 
                      onClick={() => toggleTranscript(contact.chat_id)}
                      className="w-full flex items-center justify-between px-6 py-3 text-sm font-medium text-cyan-400 hover:bg-white/5 transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" /> 
                        {expandedChatId === contact.chat_id ? 'Hide Chat Transcript' : 'View Chat Transcript'}
                      </span>
                      {expandedChatId === contact.chat_id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    
                    {expandedChatId === contact.chat_id && (
                      <div className="px-6 pb-6 pt-2">
                        {loadingChats[contact.chat_id] ? (
                          <div className="flex justify-center p-4">
                            <div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        ) : chatTranscripts[contact.chat_id]?.length > 0 ? (
                          <div className="space-y-3 bg-black/50 border border-white/5 rounded-xl p-4 max-h-60 overflow-y-auto custom-scrollbar">
                            {chatTranscripts[contact.chat_id].map((msg, idx) => (
                              <div key={msg.id || idx} className={`flex flex-col space-y-1 ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                                <div className={`flex items-center gap-1.5 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                                  {msg.role === 'user' ? (
                                    <User className="w-3 h-3 text-gray-400" />
                                  ) : (
                                    <Bot className="w-3 h-3 text-cyan-500" />
                                  )}
                                  <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                                    {msg.role === 'user' ? 'Client' : 'KAOS AI'}
                                  </span>
                                </div>
                                <div className={`max-w-[90%] text-sm px-3 py-2 rounded-xl ${msg.role === 'user' ? 'bg-cyan-500/20 text-cyan-100 border border-cyan-500/20 rounded-tr-none' : 'bg-white/5 text-gray-300 border border-white/10 rounded-tl-none'}`}>
                                  {msg.text}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 italic text-center p-4">No messages in this chat session.</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
